import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getDashboardStats } from '../../services/admin/adminApi';
import GoogleAnalyticsButton from '../../components/admin/GoogleAnalyticsButton';
import CostBreakdown from '../../components/admin/CostBreakdown';

interface DashboardData {
  users: {
    total_users: number;
    total_users_period?: number;
    new_users_today: number;
    new_users_period?: number;
    growth_rate: number;
    active_sessions: number;
    time_range?: string;
  };
  chats: {
    total_sessions: number;
    total_sessions_period?: number;
    messages_per_session: number;
    daily_counts: Array<{
      date: string;
      displayDate: string;
      chats: number;
    }>;
    time_range?: string;
  };
  personas: {
    total_created: number;
    total_created_period?: number;
    approved_count: number;
    rejected_count: number;
    all_time_approved_count?: number;
    all_time_rejected_count?: number;
    approved_personas: Array<{
      name: string;
      description: string;
      persona_name: string;
      updated_at: string;
    }>;
    rejected_personas: Array<{
      name: string;
      description: string;
      persona_name: string;
      updated_at: string;
    }>;
    time_range?: string;
  };
  research: {
    total_queries_all_time?: number;
    total_queries_period?: number;
    total_queries?: number; // For backward compatibility
    success_rate: number;
    total_cost_all_time?: number;
    total_cost_period?: number;
    total_cost?: number; // For backward compatibility
    categories: Array<{
      name: string;
      count: number;
    }>;
    recent_queries: Array<{
      query: string;
      timestamp: string;
      formatted_time?: string;
      success: boolean;
      cost?: number;
    }>;
    time_range?: string;
  };
  costs: {
    total_cost_all_time?: number;
    total_cost_period?: number;
    total_cost?: number; // For backward compatibility
    research_cost_all_time?: number;
    research_cost_period?: number;
    research_cost?: number; // For backward compatibility
    chat_cost_all_time?: number;
    chat_cost_period?: number;
    chat_cost?: number; // For backward compatibility
    persona_cost_all_time?: number;
    persona_cost_period?: number;
    persona_cost?: number; // For backward compatibility
    conductor_cost_all_time?: number;
    conductor_cost_period?: number;
    summary_cost_all_time?: number;
    summary_cost_period?: number;
    daily_costs: Array<{
      date: string;
      displayDate: string;
      cost: number;
      research_cost?: number;
      chat_cost?: number;
      persona_cost?: number;
      conductor_cost?: number;
      summary_cost?: number;
    }>;
    time_range?: string;
  };
  // Note: System/hardware stats removed - use Digital Ocean monitoring dashboard
}

const Dashboard: React.FC = () => {
  // Initialize empty data with zeros
  const emptyData = {
    users: {
      total_users: 0,
      new_users_today: 0,
      new_users_period: 0,
      growth_rate: 0,
      active_sessions: 0,
    },
    chats: {
      total_sessions: 0,
      total_sessions_period: 0,
      messages_per_session: 0,
      daily_counts: [],
    },
    personas: {
      total_created: 0,
      total_created_period: 0,
      approved_count: 0,
      rejected_count: 0,
      all_time_approved_count: 0,
      all_time_rejected_count: 0,
      approved_personas: [],
      rejected_personas: [],
    },
    research: {
      total_queries_all_time: 0,
      total_queries_period: 0,
      total_queries: 0,
      success_rate: 0,
      total_cost_all_time: 0,
      total_cost_period: 0,
      total_cost: 0,
      categories: [],
      recent_queries: [],
    },
    costs: {
      total_cost_all_time: 0,
      total_cost_period: 0,
      total_cost: 0,
      research_cost_all_time: 0,
      research_cost_period: 0,
      research_cost: 0,
      chat_cost_all_time: 0,
      chat_cost_period: 0,
      chat_cost: 0,
      persona_cost_all_time: 0,
      persona_cost_period: 0,
      persona_cost: 0,
      conductor_cost_all_time: 0,
      conductor_cost_period: 0,
      summary_cost_all_time: 0,
      summary_cost_period: 0,
      daily_costs: [],
    },
  };

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>('7d');
  const [loadingCosts, setLoadingCosts] = useState<boolean>(false);
  
  // Use a ref to prevent infinite data fetching loops
  const fetchAttempts = useRef(0);
  const maxFetchAttempts = 3;
  const lastFetchTime = useRef(0);

  // Define fetchData as a useCallback to avoid recreating on each render
  const fetchData = useCallback(async (initialLoad = false) => {
    try {
      // Prevent excessive API calls
      const now = Date.now();
      if (now - lastFetchTime.current < 2000) { // At least 2 seconds between fetches
        console.log("Skipping fetch - too soon since last attempt");
        return;
      }
      lastFetchTime.current = now;
      
      if (initialLoad) {
        setLoading(true);
        
        // Safety check to prevent infinite loops
        fetchAttempts.current += 1;
        if (fetchAttempts.current > maxFetchAttempts) {
          console.error("Max fetch attempts reached - preventing further attempts");
          setError("Failed to load dashboard data after multiple attempts. Please reload the page.");
          setLoading(false);
          return;
        }
      }
      
      console.log(`Fetching dashboard data with time range: ${timeRange}`);
      const dashboardData = await getDashboardStats(timeRange);
      
      // Debug log of received data
      console.log("Received dashboard data:", dashboardData);
      
      // Validate essential data structures to prevent render errors
      let validatedData = { ...dashboardData };
      
      // Ensure all required properties exist
      if (!validatedData.costs) validatedData.costs = emptyData.costs;
      if (!validatedData.users) validatedData.users = emptyData.users;
      if (!validatedData.chats) validatedData.chats = emptyData.chats;
      if (!validatedData.personas) validatedData.personas = emptyData.personas;
      if (!validatedData.research) validatedData.research = emptyData.research;
      
      // Ensure daily_costs is always an array
      if (!validatedData.costs.daily_costs || !Array.isArray(validatedData.costs.daily_costs)) {
        console.warn("daily_costs is missing or not an array, using empty array");
        validatedData.costs.daily_costs = [];
      }
      
      setData(validatedData);
      setError(null); // Clear any previous errors
      
      // Reset fetch attempts counter after successful fetch
      fetchAttempts.current = 0;
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      
      if (initialLoad) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to load dashboard data: ${errorMessage}`);
      }
    } finally {
      if (initialLoad) {
        setLoading(false);
      }
    }
  }, [timeRange, emptyData]);
  
  // Selective refresh for cost data only
  const fetchCostData = useCallback(async () => {
    // Don't fetch if we're already loading or if we don't have initial data
    if (loadingCosts || !data) return;
    
    try {
      setLoadingCosts(true);
      console.log('Refreshing cost data only...');
      
      // Use a dedicated endpoint for costs when available
      // For now, we're re-using the entire dashboard stats endpoint
      const dashboardData = await getDashboardStats(timeRange);
      
      // Only update the costs portion of the data if it exists
      if (dashboardData && dashboardData.costs) {
        // Validate costs data
        const costsData = dashboardData.costs;
        if (!costsData.daily_costs || !Array.isArray(costsData.daily_costs)) {
          console.warn("daily_costs is missing or not an array in cost refresh");
          costsData.daily_costs = [];
        }
        
        setData(prevData => {
          if (!prevData) return dashboardData;
          return {
            ...prevData,
            costs: costsData
          };
        });
      }
    } catch (err) {
      console.error('Failed to refresh cost data:', err);
      // Don't set global error for cost-only refresh failures
    } finally {
      setLoadingCosts(false);
    }
  }, [timeRange, data, loadingCosts]);

  // Initial data load and refresh for all data
  useEffect(() => {
    fetchData(true); // Initial load with loading indicator

    // Set up a refresh interval every 60 seconds for all data
    const interval = setInterval(() => fetchData(false), 60000);

    // Clean up on unmount
    return () => clearInterval(interval);
  }, [timeRange, fetchData]);
  
  // Set up a separate, less frequent refresh interval for cost data
  useEffect(() => {
    // Only start this after initial data load
    if (!data) return;
    
    // Refresh costs every 5 minutes (300000ms)
    const costsInterval = setInterval(fetchCostData, 300000);
    
    return () => clearInterval(costsInterval);
  }, [data, fetchCostData]);

  // Fall back to empty data if loading is stuck
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        if (loading) {
          console.warn("Loading timeout reached - falling back to empty data");
          setData(emptyData);
          setLoading(false);
          setError("Dashboard data load timed out. Some data may be unavailable.");
        }
      }, 15000); // 15 second timeout
      
      return () => clearTimeout(timeout);
    }
  }, [loading, emptyData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading dashboard data...</div>
      </div>
    );
  }

  // Rendering error boundary - if no data is available, show an error
  const hasValidData = data !== null && typeof data === 'object';
  
  if (!hasValidData) {
    return (
      <div className="p-4 text-red-500 bg-red-100 rounded-md">
        <h3 className="text-lg font-semibold">Error</h3>
        <p>Failed to load dashboard data: Invalid data format received</p>
        <button 
          onClick={() => fetchData(true)}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 bg-red-100 rounded-md">
        <h3 className="text-lg font-semibold">Error</h3>
        <p>{error}</p>
        <button 
          onClick={() => fetchData(true)}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold">Dashboard Overview</h2>
          <div className="flex items-center space-x-2">
            <label htmlFor="timeRange" className="text-sm text-gray-600">Time Range:</label>
            <select
              id="timeRange"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="p-1 text-sm border border-gray-300 rounded-md bg-white"
            >
              <option value="1d">1 Day</option>
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
        <GoogleAnalyticsButton />
      </div>
      
      {/* Top stats row */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-500">Users</h3>
          <p className="mt-2 text-3xl font-bold">
            {timeRange === 'all' 
              ? (data.users?.total_users || 0) 
              : (data.users?.total_users_period || data.users?.total_users || 0)}
          </p>
          <p className="mt-2 text-sm text-green-500">
            +{(data.users?.new_users_period || data.users?.new_users_today || 0)} new {timeRange === '1d' ? 'today' : `in ${timeRange}`} ({(data.users?.growth_rate || 0).toFixed(1)}% growth)
          </p>
        </div>

        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-500">Chat Sessions</h3>
          <p className="mt-2 text-3xl font-bold">
            {timeRange === 'all' 
              ? (data.chats?.total_sessions || 0) 
              : (data.chats?.total_sessions_period || data.chats?.total_sessions || 0)}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {(data.chats?.messages_per_session || 0).toFixed(1)} messages per session
          </p>
        </div>

        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-500">API Costs</h3>
          <p className="mt-2 text-3xl font-bold">
            ${(timeRange === 'all' 
              ? (data.costs?.total_cost_all_time || data.costs?.total_cost || 0) 
              : (data.costs?.total_cost_period || data.costs?.total_cost || 0)).toFixed(2)}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Categories: Chat, Research, Persona, Conductor
          </p>
        </div>
      </div>

      {/* Cost Analysis Section */}
      <div className="relative">
        {loadingCosts && (
          <div className="absolute top-2 right-2 flex items-center text-xs text-blue-500">
            <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Refreshing cost data...
          </div>
        )}
        {data.costs && (
          <CostBreakdown costData={{
            total_cost_period: timeRange === 'all' 
              ? (data.costs.total_cost_all_time || data.costs.total_cost || 0) 
              : (data.costs.total_cost_period || data.costs.total_cost || 0),
            chat_cost_period: timeRange === 'all' 
              ? (data.costs.chat_cost_all_time || data.costs.chat_cost || 0) 
              : (data.costs.chat_cost_period || data.costs.chat_cost || 0),
            research_cost_period: timeRange === 'all' 
              ? (data.costs.research_cost_all_time || data.costs.research_cost || 0) 
              : (data.costs.research_cost_period || data.costs.research_cost || 0),
            persona_cost_period: timeRange === 'all' 
              ? (data.costs.persona_cost_all_time || data.costs.persona_cost || 0) 
              : (data.costs.persona_cost_period || data.costs.persona_cost || 0),
            conductor_cost_period: timeRange === 'all' 
              ? (data.costs.conductor_cost_all_time || 0) 
              : (data.costs.conductor_cost_period || 0),
            summary_cost_period: timeRange === 'all' 
              ? (data.costs.summary_cost_all_time || 0) 
              : (data.costs.summary_cost_period || 0),
            daily_costs: data.costs.daily_costs || [],
            time_range: timeRange
          }} />
        )}
      </div>

      {/* Persona section */}
      {data.personas && (
        <div className="p-6 bg-white rounded-lg shadow-md">
          <h3 className="mb-4 text-lg font-semibold">Persona Status</h3>
          
          <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-3">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-xl font-bold text-blue-600">
                {timeRange === 'all' 
                  ? (data.personas.total_created || 0) 
                  : (data.personas.total_created_period || data.personas.total_created || 0)}
              </p>
              <p className="text-sm text-gray-600">Total Personas {timeRange !== 'all' ? `(${timeRange})` : ''}</p>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-xl font-bold text-green-600">
                {timeRange === 'all' 
                  ? (data.personas.all_time_approved_count || data.personas.approved_count || 0) 
                  : (data.personas.approved_count || 0)}
              </p>
              <p className="text-sm text-gray-600">Approved {timeRange !== 'all' ? `(${timeRange})` : ''}</p>
            </div>
            
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-xl font-bold text-red-600">
                {timeRange === 'all' 
                  ? (data.personas.all_time_rejected_count || data.personas.rejected_count || 0) 
                  : (data.personas.rejected_count || 0)}
              </p>
              <p className="text-sm text-gray-600">Rejected {timeRange !== 'all' ? `(${timeRange})` : ''}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h4 className="mb-2 text-md font-semibold">Recently Approved</h4>
              <ul className="space-y-2">
                {data.personas.approved_personas && data.personas.approved_personas.length > 0 ? (
                  data.personas.approved_personas.slice(0, 5).map((persona, index) => (
                    <li key={index} className="p-2 bg-gray-50 rounded">
                      <p className="font-medium">{persona.name}</p>
                      <p className="text-sm text-gray-600">{persona.description?.substring(0, 80) || ''}...</p>
                    </li>
                  ))
                ) : (
                  <li className="p-2 bg-gray-50 rounded text-gray-500">No approved personas available</li>
                )}
              </ul>
            </div>
            
            <div>
              <h4 className="mb-2 text-md font-semibold">Recently Rejected</h4>
              <ul className="space-y-2">
                {data.personas.rejected_personas && data.personas.rejected_personas.length > 0 ? (
                  data.personas.rejected_personas.slice(0, 5).map((persona, index) => (
                    <li key={index} className="p-2 bg-gray-50 rounded">
                      <p className="font-medium">{persona.name}</p>
                      <p className="text-sm text-gray-600">{persona.description?.substring(0, 80) || ''}...</p>
                    </li>
                  ))
                ) : (
                  <li className="p-2 bg-gray-50 rounded text-gray-500">No rejected personas available</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Note: System/hardware stats removed - use Digital Ocean monitoring dashboard */}
    </div>
  );
};

export default Dashboard;